import type { ReactNode } from "react";

import { cn } from "../../utils";

export interface SettingsRowProps {
  /** Primary label (left column). */
  label: ReactNode;
  /** Optional secondary description below the label. */
  description?: ReactNode;
  /**
   * The control rendered on the right — a `<Switch>`, `<Button>`,
   * `<Select>`, `<Input>`, etc.
   */
  control?: ReactNode;
  /**
   * When true, render list-row chrome (`border-b` divider + vertical
   * padding, self-aware of first/last). Use when stacking several rows in
   * a plain container so they read as one divided list — matches the
   * list-row pattern in CLAUDE.md rule #5. Default false (bare row, for a
   * single row inside a `SettingsSection`).
   */
  divided?: boolean;
  /** Extra content below the label/description (e.g. a status line). */
  children?: ReactNode;
  className?: string;
}

/**
 * The canonical "label + description on the left, control on the right"
 * settings row (CLAUDE.md Hard Rule 8). Before this existed the same
 * `flex items-start justify-between gap-4` markup was hand-rolled in every
 * settings form (autopilot, optimization, verdicts, products, …). Compose
 * this inside a {@link SettingsSection}; pass `divided` when stacking rows
 * into a single bordered list.
 */
export function SettingsRow({
  label,
  description,
  control,
  divided = false,
  children,
  className,
}: SettingsRowProps) {
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-4",
        // Divided rows adopt the shared --row-py rhythm (RowShell metrics,
        // VERTICAL-RHYTHM-PRD §T3 / D3); the first/last edge trims are settings-
        // list specific and kept.
        divided &&
          "border-b border-border/40 py-[var(--row-py)] first:pt-0 last:border-b-0 last:pb-0",
        className,
      )}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <span className="type-body text-foreground">{label}</span>
        {description ? (
          <span className="type-subtext text-muted-foreground">{description}</span>
        ) : null}
        {children}
      </div>
      {control ? <div className="shrink-0">{control}</div> : null}
    </div>
  );
}
